import { AxiosError } from 'axios';

export interface ExtractedError {
  errorCode: string;
  errorMessage: string;
  httpStatus?: number;
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

function toString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function hasAccountManagementErrors(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;

  const obj = data as Record<string, unknown>;
  if (!('accountManagement' in obj)) return false;

  const accountManagement = obj.accountManagement;
  if (typeof accountManagement !== 'object' || accountManagement === null)
    return false;

  const am = accountManagement as Record<string, unknown>;
  return Array.isArray(am.errors) && am.errors.length > 0;
}

function hasHttpErrorFields(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  return 'status' in obj && 'error' in obj;
}

export function extractRewardsError(error: AxiosError): ExtractedError {
  const responseData = error.response?.data as
    | Record<string, unknown>
    | undefined;
  const httpStatus = error.response?.status;

  if (responseData && hasAccountManagementErrors(responseData)) {
    const am = responseData.accountManagement as Record<string, unknown>;
    const errors = am.errors as Array<Record<string, unknown>>;
    const firstError = errors[0];

    return {
      errorCode: toString(firstError?.errorCode) ?? 'UNKNOWN_ERROR_CODE',
      errorMessage:
        toString(firstError?.errorMessage) ?? 'No error message provided',
      httpStatus,
    };
  }

  if (responseData && hasHttpErrorFields(responseData)) {
    const status = toNumber(responseData.status);
    const errorMsg = toString(responseData.error);

    return {
      errorCode: status !== undefined ? `HTTP_${status}` : 'HTTP_ERROR',
      errorMessage: errorMsg ?? 'Unknown error',
      httpStatus: status,
    };
  }

  return {
    errorCode:
      error.code ?? (httpStatus ? `HTTP_${httpStatus}` : 'NETWORK_ERROR'),
    errorMessage: error.message || 'Network error occurred',
    httpStatus,
  };
}
